import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { prism } from "react-syntax-highlighter/dist/esm/styles/prism";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext"; // For user initials, if needed
import config from "../config";
import plusIcon from "../public/img/plus.svg"; // For chat input
import arrow from "../public/img/arrow.svg"; // For chat input
import copyIcon from "../public/img/copy.svg"; // For StyledCodeBlock
import ltLogo from "../public/img/LT_logo.svg"; // Import the logo
import caretIcon from "../public/img/caret.svg"; // Import the caret icon
import downCaretIcon from "../public/img/down_caret.svg"; // Import the down caret icon
import "./Chat.css"; // Re-use chat styling for now
import "./Toolbox.css"; // Specific styles for Toolbox

// Helper to construct WebSocket URL for Toolbox
const getToolboxWebSocketURL = () => {
  const baseUrl = config.api.baseUrl;
  let wsUrl = baseUrl.replace(/^http/, "ws");
  wsUrl += "/toolbox/ws"; // Corrected: Removed /chatbot prefix
  return wsUrl;
};

// StyledCodeBlock copied from Chat.js for consistency
const StyledCodeBlock = ({ language, children }) => {
  const [copySuccess, setCopySuccess] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const codeString = String(children).replace(/\n$/, "");
  const isHtml = language?.toLowerCase() === "html";

  const handleCopy = () => {
    navigator.clipboard.writeText(codeString).then(
      () => {
        setCopySuccess("Copied!");
        setTimeout(() => setCopySuccess(""), 2000);
      },
      () => {
        setCopySuccess("Failed");
        setTimeout(() => setCopySuccess(""), 2000);
      }
    );
  };

  const togglePreview = () => setShowPreview(!showPreview);

  return (
    <div className="styled-code-block-container">
      <div className="styled-code-block-header">
        <span className="styled-code-block-language">{language || "code"}</span>
        <div className="styled-code-block-header-buttons">
          {isHtml && (
            <button
              onClick={togglePreview}
              className="styled-code-block-toggle-button"
              title={showPreview ? "Show Code" : "Preview HTML"}
            >
              {showPreview ? "Show Code" : "Preview HTML"}
            </button>
          )}
          <span
            onClick={handleCopy}
            className="styled-code-block-copy-button"
            title={copySuccess || "Copy code"}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && handleCopy()
            }
          >
            <img src={copyIcon} alt="Copy code" className="copy-icon-svg" />
          </span>
        </div>
      </div>
      {isHtml && showPreview ? (
        <iframe
          className="styled-code-block-html-preview"
          srcDoc={codeString}
          sandbox="allow-scripts allow-same-origin"
          title="HTML Preview"
        />
      ) : (
        <SyntaxHighlighter
          style={prism}
          language={language}
          customStyle={{ margin: 0, borderRadius: "0 0 6px 6px" }}
        >
          {codeString}
        </SyntaxHighlighter>
      )}
    </div>
  );
};

// Utility function to get model details (including provider)
const getModelDetails = (modelId, modelsByProvider) => {
  if (
    !modelId ||
    !modelsByProvider ||
    Object.keys(modelsByProvider).length === 0
  )
    return null;
  for (const provider in modelsByProvider) {
    if (modelsByProvider[provider]) {
      // Check if provider array exists
      const modelInfo = modelsByProvider[provider].find(
        (m) => m.id === modelId
      );
      if (modelInfo) {
        return { ...modelInfo, provider: provider }; // Add provider to the model info
      }
    }
  }
  return null;
};

const Toolbox = () => {
  const { user } = useAuth();

  // Configuration State
  const [model, setModel] = useState(""); // Default model - will be set by fetched models
  const [systemPrompt, setSystemPrompt] = useState(
    "You are a helpful assistant."
  );
  const [temperature, setTemperature] = useState(1.0);
  const [availableFunctions, setAvailableFunctions] = useState([]);
  const [selectedFunctionIds, setSelectedFunctionIds] = useState([]); // Store IDs of selected functions

  // Model fetching state
  const [availableModels, setAvailableModels] = useState({}); // Stores models grouped by provider
  const [fetchingModels, setFetchingModels] = useState(true);
  const [fetchModelsError, setFetchModelsError] = useState(null);

  // Chat State (similar to Chat.js)
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  // WebSocket State
  const wsInstance = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [chatError, setChatError] = useState(null);
  const currentBotMessageId = useRef(null); // Tracks the ID of the bot message being streamed

  const [fetchingFunctions, setFetchingFunctions] = useState(true);
  const [fetchFunctionsError, setFetchFunctionsError] = useState(null);

  const [isParamsSidebarOpen, setIsParamsSidebarOpen] = useState(true);
  const [isFunctionsDropdownOpen, setIsFunctionsDropdownOpen] = useState(false); // New state for functions dropdown
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false); // State for custom model dropdown
  const [pendingModelSwitchInfo, setPendingModelSwitchInfo] = useState(null); // For model switch confirmation

  // ADD THIS: Derived state to control WebSocket connection presence
  const hasModel = !!model.trim();

  // Store config in refs to ensure onopen and submitMessage always get the latest values
  // without needing to be in the WebSocket useEffect dependency array directly for re-connection logic.
  const modelRef = useRef(model);
  const systemPromptRef = useRef(systemPrompt);
  const temperatureRef = useRef(temperature);
  const selectedFunctionIdsRef = useRef(selectedFunctionIds);
  const availableFunctionsRef = useRef(availableFunctions);
  const availableModelsRef = useRef(availableModels);

  useEffect(() => {
    modelRef.current = model;
  }, [model]);
  useEffect(() => {
    systemPromptRef.current = systemPrompt;
  }, [systemPrompt]);
  useEffect(() => {
    temperatureRef.current = temperature;
  }, [temperature]);
  useEffect(() => {
    selectedFunctionIdsRef.current = selectedFunctionIds;
  }, [selectedFunctionIds]);
  useEffect(() => {
    availableFunctionsRef.current = availableFunctions;
  }, [availableFunctions]);
  useEffect(() => {
    availableModelsRef.current = availableModels;
  }, [availableModels]);

  // Fetch available models from the backend
  useEffect(() => {
    const fetchModels = async () => {
      setFetchingModels(true);
      setFetchModelsError(null);
      try {
        // Assuming your admin blueprint is mounted at /admin
        const response = await fetch(
          `${config.api.baseUrl}/admin/models/grouped`,
          { credentials: "include" }
        );
        const data = await response.json();
        if (data.success && typeof data.models === "object") {
          // Order the provider groups; models inside each group keep the
          // backend's ranked order.
          const specificProviderOrder = ["openai", "google", "anthropic"];
          const modelProviders = Object.keys(data.models);

          const sortedProviderKeys = [
            ...specificProviderOrder.filter((p) => modelProviders.includes(p)),
            ...modelProviders
              .filter((p) => !specificProviderOrder.includes(p))
              .sort(),
          ];

          const sortedModels = {};
          sortedProviderKeys.forEach((provider) => {
            if (data.models[provider]) {
              // Preserve the backend's rank-sorted order within each provider group
              sortedModels[provider] = data.models[provider];
            }
          });

          setAvailableModels(sortedModels);

          // Set default model: first model from the first sorted provider
          if (sortedProviderKeys.length > 0) {
            const firstProvider = sortedProviderKeys[0];
            if (
              sortedModels[firstProvider] &&
              sortedModels[firstProvider].length > 0
            ) {
              setModel(sortedModels[firstProvider][0].id);
            }
          }
        } else {
          throw new Error(data.error || "Failed to fetch models list");
        }
      } catch (err) {
        setFetchModelsError(err.message || "Could not load models.");
        setAvailableModels({});
      } finally {
        setFetchingModels(false);
      }
    };
    fetchModels();
  }, []);

  // Fetch available functions from the backend
  useEffect(() => {
    const fetchFunctions = async () => {
      setFetchingFunctions(true);
      setFetchFunctionsError(null);
      try {
        const response = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/functions`,
          { credentials: "include" }
        );
        const data = await response.json();
        if (data.success && Array.isArray(data.functions)) {
          setAvailableFunctions(data.functions);
        } else {
          throw new Error(data.error || "Failed to fetch functions list");
        }
      } catch (err) {
        setFetchFunctionsError(err.message || "Could not load functions.");
        setAvailableFunctions([]);
      } finally {
        setFetchingFunctions(false);
      }
    };
    fetchFunctions();
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // WebSocket connection management
  useEffect(() => {
    // Use hasModel to determine connection state
    if (!hasModel) {
      if (
        wsInstance.current &&
        (wsInstance.current.readyState === WebSocket.OPEN ||
          wsInstance.current.readyState === WebSocket.CONNECTING)
      ) {
        wsInstance.current.onclose = null;
        wsInstance.current.close(1000, "Model cleared, disconnecting Toolbox");
        wsInstance.current = null;
      }
      setIsConnected(false);
      setIsConnecting(false);
      setChatError(null); // Clear error when no model
      return;
    }

    // If hasModel is true, but we are already connected and the instance is valid, do nothing.
    if (
      wsInstance.current &&
      wsInstance.current.readyState === WebSocket.OPEN
    ) {
      return;
    }

    if (
      wsInstance.current &&
      (wsInstance.current.readyState === WebSocket.CLOSING ||
        wsInstance.current.readyState === WebSocket.CLOSED)
    ) {
      wsInstance.current.onclose = null;
      wsInstance.current = null;
    }

    setIsConnecting(true);
    setIsConnected(false);
    setChatError(null);
    currentBotMessageId.current = null;

    const ws = new WebSocket(getToolboxWebSocketURL());
    wsInstance.current = ws;

    ws.onopen = () => {
      setIsConnecting(false);
      setIsConnected(true);
      setChatError(null);

      // Use refs to get current values for the init message
      const currentSelectedFunctions = availableFunctionsRef.current.filter(
        (func) => selectedFunctionIdsRef.current.includes(func._id)
      );

      let providerForInit = "openai";
      if (
        modelRef.current &&
        availableModelsRef.current &&
        Object.keys(availableModelsRef.current).length > 0
      ) {
        for (const prov in availableModelsRef.current) {
          if (
            availableModelsRef.current[prov] &&
            Array.isArray(availableModelsRef.current[prov])
          ) {
            if (
              availableModelsRef.current[prov].find(
                (m) => m.id === modelRef.current
              )
            ) {
              providerForInit = prov;
              break;
            }
          }
        }
      }

      ws.send(
        JSON.stringify({
          type: "init",
          config: {
            provider: providerForInit,
            model: modelRef.current,
            prompt: systemPromptRef.current,
            temperature: parseFloat(temperatureRef.current),
            selected_functions: currentSelectedFunctions,
          },
        })
      );
    };

    ws.onmessage = (event) => {
      let serverMessage;
      try {
        serverMessage = JSON.parse(event.data);
      } catch (e) {
        setChatError("Received an invalid message from the server.");
        return;
      }

      switch (serverMessage.type) {
        case "init_success":
          setChatError(null);
          break;
        case "response_created":
          currentBotMessageId.current = serverMessage.response_id;
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              id: serverMessage.response_id,
              role: "assistant",
              content: "",
              timestamp: new Date().toISOString(),
            },
          ]);
          break;
        case "text_delta":
          if (currentBotMessageId.current && serverMessage.delta) {
            setMessages((prevMessages) =>
              prevMessages.map((msg) =>
                msg.id === currentBotMessageId.current
                  ? { ...msg, content: msg.content + serverMessage.delta }
                  : msg
              )
            );
          }
          break;
        case "reasoning_delta":
          if (serverMessage.delta) {
            console.log(
              `[${serverMessage.provider || "UnknownProvider"} Reasoning]: ${
                serverMessage.delta
              }`
            );
          }
          break;
        case "response_completed":
          currentBotMessageId.current = null;
          scrollToBottom();
          break;
        case "error":
          setChatError(
            serverMessage.message || "An error occurred with the chat server."
          );
          break;
        case "ping":
          break;
        default:
          console.warn(
            "Toolbox.js: Unhandled WebSocket message type:",
            serverMessage.type
          );
      }
    };

    ws.onerror = (errorEvent) => {
      setIsConnecting(false);
      setIsConnected(false);
      setChatError(
        "WebSocket connection error. Please try reconnecting or check server."
      );
    };

    ws.onclose = (event) => {
      setIsConnecting(false);
      setIsConnected(false);
      currentBotMessageId.current = null;
      if (
        !event.wasClean &&
        event.code !== 1000 &&
        event.code !== 1001 &&
        event.reason !== "Model cleared, disconnecting Toolbox"
      ) {
        // Avoid showing error for explicit client-side closes (1000/1001 or our custom reason)
        setChatError(
          `Disconnected (Code: ${event.code}). Please refresh or check server.`
        );
      } else if (event.reason === "Model cleared, disconnecting Toolbox") {
        setChatError(null); // Clear any previous error if we are intentionally disconnecting
      }
    };

    return () => {
      if (wsInstance.current) {
        wsInstance.current.onclose = null;
        wsInstance.current.close(
          1000,
          "Toolbox WebSocket cleanup: hasModel became false or unmounting"
        );
        wsInstance.current = null;
      }
      setIsConnected(false);
      setIsConnecting(false);
      // Don't setChatError here, as it might overwrite a legitimate error from onclose
    };
  }, [
    hasModel,
    scrollToBottom,
    // Refs are not needed in dependency array as they don't trigger re-renders.
    // The values they point to are always current.
  ]);

  const handleTextareaResize = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "60px"; // Min height from Chat.css
      if (textarea.scrollHeight > textarea.clientHeight) {
        textarea.style.height = `${Math.max(60, textarea.scrollHeight)}px`;
      }
    }
  };

  const resetTextareaHeight = () => {
    if (textareaRef.current) textareaRef.current.style.height = "60px";
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    handleTextareaResize();
    if (e.target.value === "") resetTextareaHeight();
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUserInitials = () => {
    if (!user) return "?";
    let initials = "";
    if (user.first_name) initials += user.first_name.charAt(0).toUpperCase();
    if (user.last_name) initials += user.last_name.charAt(0).toUpperCase();
    return initials || user.email?.charAt(0).toUpperCase() || "?";
  };

  const submitMessage = (content) => {
    if (
      !content.trim() ||
      !wsInstance.current ||
      wsInstance.current.readyState !== WebSocket.OPEN
    ) {
      if (!isConnected) setChatError("Cannot send: Not connected.");
      return;
    }

    const userMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: content.trim(),
      timestamp: new Date().toISOString(),
    };
    setMessages((prevMessages) => [...prevMessages, userMessage]);

    setChatError(null);

    // Use refs to get current config for sending the message
    const currentSelectedFunctions = availableFunctionsRef.current.filter(
      (func) => selectedFunctionIdsRef.current.includes(func._id)
    );
    let providerForMessage = "openai";
    if (
      modelRef.current &&
      availableModelsRef.current &&
      Object.keys(availableModelsRef.current).length > 0
    ) {
      for (const prov in availableModelsRef.current) {
        if (
          availableModelsRef.current[prov] &&
          Array.isArray(availableModelsRef.current[prov])
        ) {
          if (
            availableModelsRef.current[prov].find(
              (m) => m.id === modelRef.current
            )
          ) {
            providerForMessage = prov;
            break;
          }
        }
      }
    }

    wsInstance.current.send(
      JSON.stringify({
        type: "user_message",
        content: userMessage.content,
        current_config: {
          provider: providerForMessage,
          model: modelRef.current,
          prompt: systemPromptRef.current,
          temperature: parseFloat(temperatureRef.current),
          selected_functions: currentSelectedFunctions,
        },
      })
    );
  };

  const handleSendMessage = () => {
    submitMessage(inputText);
    setInputText("");
    resetTextareaHeight();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (e.metaKey || e.ctrlKey) {
        // Cmd/Ctrl + Enter: Insert newline
        // Allow default behavior for textarea to insert newline on Ctrl/Cmd + Enter
        // Or, more explicitly:
        // setInputText((prev) => prev + "\n");
        // handleTextareaResize(); // Adjust height if newline added
        // e.preventDefault(); // Prevent any other default action if needed, but usually not for this case
      } else if (!e.shiftKey) {
        // Enter alone (and not Shift+Enter, which is default newline)
        e.preventDefault(); // Prevent newline
        handleSendMessage();
      }
      // If e.shiftKey is true, default behavior (newline) is allowed implicitly
    }
  };

  const handleFunctionSelectionChange = (functionId) => {
    setSelectedFunctionIds((prevSelected) =>
      prevSelected.includes(functionId)
        ? prevSelected.filter((id) => id !== functionId)
        : [...prevSelected, functionId]
    );
  };

  const toggleParamsSidebar = () =>
    setIsParamsSidebarOpen(!isParamsSidebarOpen);

  const toggleFunctionsDropdown = () =>
    setIsFunctionsDropdownOpen(!isFunctionsDropdownOpen); // Toggle function

  const toggleModelDropdown = () =>
    setIsModelDropdownOpen(!isModelDropdownOpen);

  const handleModelSelect = (selectedModelId) => {
    const currentModelDetails = getModelDetails(
      modelRef.current,
      availableModelsRef.current
    );
    const newModelDetails = getModelDetails(
      selectedModelId,
      availableModelsRef.current
    );

    if (!newModelDetails) {
      setIsModelDropdownOpen(false);
      return;
    }

    if (
      messages.length > 0 &&
      currentModelDetails &&
      newModelDetails.provider !== currentModelDetails.provider
    ) {
      setPendingModelSwitchInfo({
        newModelId: newModelDetails.id,
        newModelName: newModelDetails.name,
        newProvider: newModelDetails.provider,
        oldModelId: currentModelDetails.id,
        oldModelName: currentModelDetails.name,
        oldProvider: currentModelDetails.provider,
      });
      setIsModelDropdownOpen(false); // Close dropdown, wait for user decision
    } else {
      setModel(selectedModelId);
      setPendingModelSwitchInfo(null); // Clear any pending switch if conditions not met
      setIsModelDropdownOpen(false);
    }
  };

  const handleConfirmSwitch = () => {
    if (pendingModelSwitchInfo) {
      setModel(pendingModelSwitchInfo.newModelId);
      setMessages([]); // Clear chat history
      setPendingModelSwitchInfo(null);
    }
  };

  const handleCancelSwitch = () => {
    setPendingModelSwitchInfo(null);
  };

  const getSelectedModelName = () => {
    const details = getModelDetails(model, availableModelsRef.current);
    if (details) return details.name;
    if (model) return model;
    return "Select a model";
  };

  return (
    <Layout>
      <div
        className={`toolbox-page-container ${
          isParamsSidebarOpen ? "params-open" : "params-closed"
        }`}
      >
        <button
          onClick={toggleParamsSidebar}
          className="params-sidebar-toggle-btn"
          title={isParamsSidebarOpen ? "Close Parameters" : "Open Parameters"}
        >
          <img
            src={caretIcon}
            alt="Params"
            className={`params-toggle-caret ${
              isParamsSidebarOpen ? "open" : "closed"
            }`}
          />
        </button>

        <div className="toolbox-config-sidebar-wrapper">
          <div className="toolbox-config-panel">
            <h2>AI Toolbox Configuration</h2>
            <div className="toolbox-form-group">
              <label htmlFor="model">Model ID:</label>
              {fetchingModels && (
                <p className="loading-text small-loading">Loading models...</p>
              )}
              {fetchModelsError && (
                <p className="error-message small-error">{fetchModelsError}</p>
              )}
              {!fetchingModels &&
                Object.keys(availableModels).length === 0 &&
                !fetchModelsError && (
                  <p className="info-text small-info">No models available.</p>
                )}
              {!fetchingModels && Object.keys(availableModels).length > 0 && (
                <div className="custom-dropdown-container model-dropdown-container">
                  <button
                    type="button"
                    className="custom-dropdown-toggle model-dropdown-toggle"
                    onClick={toggleModelDropdown}
                    disabled={fetchingModels}
                  >
                    <span>{getSelectedModelName()}</span>
                    <img
                      src={downCaretIcon}
                      alt="Toggle Models"
                      className={`custom-dropdown-icon ${
                        isModelDropdownOpen ? "open" : ""
                      }`}
                    />
                  </button>
                  {isModelDropdownOpen && (
                    <div className="custom-dropdown-panel model-dropdown-panel">
                      {(() => {
                        const specificProviderOrder = [
                          "openai",
                          "google",
                          "anthropic",
                        ];
                        const modelProviders = Object.keys(availableModels);
                        const sortedProviderKeys = [
                          ...specificProviderOrder.filter((p) =>
                            modelProviders.includes(p)
                          ),
                          ...modelProviders
                            .filter((p) => !specificProviderOrder.includes(p))
                            .sort(),
                        ];
                        return sortedProviderKeys.map((provider) => {
                          const modelsInGroup = availableModels[provider];
                          if (!modelsInGroup || modelsInGroup.length === 0)
                            return null;
                          return (
                            <React.Fragment key={provider}>
                              <div className="custom-dropdown-optgroup-label">
                                {provider}
                              </div>
                              {modelsInGroup.map((m) => (
                                <div
                                  key={m.id}
                                  className={`custom-dropdown-item ${
                                    model === m.id ? "selected" : ""
                                  }`}
                                  onClick={() => handleModelSelect(m.id)}
                                >
                                  {m.name} ({m.id})
                                </div>
                              ))}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="toolbox-form-group">
              <label htmlFor="systemPrompt">System Prompt:</label>
              <textarea
                id="systemPrompt"
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                rows={4}
              ></textarea>
            </div>
            <div className="toolbox-form-group">
              <label htmlFor="temperature">
                Temperature: {temperature.toFixed(1)}
              </label>
              <input
                type="range"
                id="temperature"
                min="0"
                max="2"
                step="0.1"
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
              />
            </div>
            <div className="toolbox-form-group functions-group">
              <label>Available Functions:</label>
              <div className="functions-dropdown-container">
                <button
                  type="button"
                  className="functions-dropdown-toggle"
                  onClick={toggleFunctionsDropdown}
                >
                  Select Functions ({selectedFunctionIds.length} selected)
                  <img
                    src={downCaretIcon}
                    alt="Toggle Functions"
                    className={`functions-dropdown-icon ${
                      isFunctionsDropdownOpen ? "open" : ""
                    }`}
                  />
                </button>
                {isFunctionsDropdownOpen && (
                  <div className="functions-checkbox-group dropdown-active">
                    {fetchingFunctions && (
                      <p className="loading-text small-loading">
                        Loading functions...
                      </p>
                    )}
                    {fetchFunctionsError && (
                      <p className="error-message small-error">
                        {fetchFunctionsError}
                      </p>
                    )}
                    {!fetchingFunctions &&
                      availableFunctions.length === 0 &&
                      !fetchFunctionsError && (
                        <p className="info-text small-info">
                          No functions available.
                        </p>
                      )}
                    {availableFunctions.map((func) => (
                      <div key={func._id} className="function-checkbox-item">
                        <input
                          type="checkbox"
                          id={`func-${func._id}`}
                          value={func._id}
                          checked={selectedFunctionIds.includes(func._id)}
                          onChange={() =>
                            handleFunctionSelectionChange(func._id)
                          }
                        />
                        <label
                          htmlFor={`func-${func._id}`}
                          title={func.description}
                        >
                          {func.name}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="toolbox-chat-panel">
          {pendingModelSwitchInfo && (
            <div className="provider-switch-warning-overlay">
              <div className="provider-switch-warning-content">
                <h4>Confirm Model Switch</h4>
                <p>
                  You are attempting to switch from model{" "}
                  <strong>{pendingModelSwitchInfo.oldModelName}</strong> (
                  {pendingModelSwitchInfo.oldProvider}) to model{" "}
                  <strong>{pendingModelSwitchInfo.newModelName}</strong> (
                  {pendingModelSwitchInfo.newProvider}).
                </p>
                <p>
                  Switching to a model from a different provider will clear your
                  current chat history.
                </p>
                <div className="provider-switch-warning-actions">
                  <button
                    onClick={handleCancelSwitch}
                    className="button-secondary"
                  >
                    Back to {pendingModelSwitchInfo.oldModelName}
                  </button>
                  <button
                    onClick={handleConfirmSwitch}
                    className="button-primary"
                  >
                    Switch to {pendingModelSwitchInfo.newProvider} & Clear Chat
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="chat-container toolbox-chat-container">
            <div className="chat-div">
              {isConnecting && (
                <div className="info-message connecting">
                  Connecting to Toolbox...
                </div>
              )}
              {!isConnected && !isConnecting && chatError && (
                <div className="chat-error-overlay static-error">
                  {chatError}
                </div>
              )}
              {!isConnected && !isConnecting && !chatError && model.trim() && (
                <div className="info-message disconnected">
                  Connecting with current settings...
                </div>
              )}
              {!isConnected && !isConnecting && !model.trim() && (
                <div className="info-message disconnected">
                  Please enter a Model ID to connect.
                </div>
              )}
              <div className="chat-messages">
                {messages.length === 0 && (
                  <div className="empty-chat-fullheight">
                    <p className="empty-chat-description">
                      Configure parameters and start chatting.
                    </p>
                  </div>
                )}
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`chat-message ${
                      message.role === "user" ? "user" : "bot"
                    }`}
                  >
                    {message.role === "user" ? (
                      <div className="message-avatar user-avatar">
                        <span>{getUserInitials()}</span>
                      </div>
                    ) : (
                      <div className="message-avatar bot-avatar">
                        <div className="bot-icon default-icon">
                          <img
                            src={ltLogo}
                            alt="AI Avatar"
                            className="bot-avatar-svg"
                          />
                        </div>
                      </div>
                    )}
                    <div className="message-wrapper">
                      <div className="message-content">
                        {message.role === "assistant" ? (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              code({
                                node,
                                inline,
                                className,
                                children,
                                ...props
                              }) {
                                const match = /language-(\w+)/.exec(
                                  className || ""
                                );
                                return !inline && match ? (
                                  <StyledCodeBlock language={match[1]}>
                                    {children}
                                  </StyledCodeBlock>
                                ) : (
                                  <code className={className} {...props}>
                                    {children}
                                  </code>
                                );
                              },
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        ) : (
                          message.content
                        )}
                      </div>
                      <div className="message-timestamp">
                        {formatTime(message.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              {chatError && isConnected && (
                <div className="chat-error-overlay">{chatError}</div>
              )}
              <div className="chat-input-container">
                <textarea
                  ref={textareaRef}
                  className="chat-input"
                  placeholder={
                    isConnected
                      ? "Ask anything..."
                      : "Waiting for connection..."
                  }
                  value={inputText}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  disabled={!isConnected || !model.trim()}
                />
                <div className="buttons-container">
                  <button
                    className="circular-button plus-button"
                    disabled={!isConnected || !model.trim()}
                  >
                    <img src={plusIcon} alt="Add" className="plus-icon" />
                  </button>
                  <button
                    className="circular-button send-button"
                    disabled={
                      !isConnected || !inputText.trim() || !model.trim()
                    }
                    onClick={handleSendMessage}
                  >
                    <img src={arrow} alt="Send" className="send-icon" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Toolbox;
